import VuexPersist from 'vuex-persist';
import { get, set } from 'lodash';
import debounce from 'lodash/debounce';
import { useRafFn } from '@vueuse/core';

const LOCAL_STORAGE_KEY = 'vuexPersistV3';

const storesToPersist = {
  view: ['locale'],
  viewOptions: ['sortBy', 'sortDesc', 'filter', 'columnWidths'],
  viewsMenu: ['miniSidebar'],
  workspace: ['workspace', 'currentCategoryName', 'newVersion', 'currentScope'],
  apps: ['selectedAreasOfInterest', 'selectedAppsPerSelectedAreaOfInterest'],
  userConfigs: ['favoriteViewIds', 'favoriteAppIds'],
};

const SAFELIST = Object.entries(storesToPersist).reduce((acc, [storeName, keys]) => {
  keys.forEach((key) => {
    acc.push(`${storeName}.${key}`);
  });

  return acc;
}, [] as string[]);

const debouncedAndRaf = fn => debounce((...args) => {
  useRafFn(() => fn(...args), { once: true });
}, 1000);

const debounceSetItem = debouncedAndRaf((key, value) => {
  if (!window.localStorage) return;

  try {
    const finalValue = JSON.stringify(SAFELIST.reduce((acc, path) => {
      set(acc, path, get(value, path));
      return acc;
    }, {}));

    if (window.localStorage.getItem(key) === finalValue) return;
    window.localStorage.setItem(key, finalValue);
  } catch {
    window.localStorage.removeItem(key);
  }
});

export const vuexPersistKey = () => `${LOCAL_STORAGE_KEY}-${useRoute().params.tenantSlug || ''}`;

// Override saveState to prevent JSON.stringify and JSON.parse twice
const saveState = ((key, state, storage) => {
  storage?.setItem(key, state);
});

export default defineNuxtPlugin({
  name: 'vuex-persist',
  dependsOn: ['store'],
  setup({ $store }) {
    new VuexPersist({
      key: vuexPersistKey(), // The key to store the state on in the storage provider.
      modules: Object.keys(storesToPersist),
      saveState,
      storage: {
        ...window.localStorage,
        // Keep this function defined to dont break vue persistence getItem
        getItem(key) {
          return window.localStorage?.getItem(key);
        },
        setItem: debounceSetItem,
      }, // or window.sessionStorage or localForage
    }).plugin($store);
  },
});
